import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCheck } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { RadioGroup, RadioGroupItem } from '@vaiu/ui/radio-group';
import { Label } from '@vaiu/ui/label';
import { i18n } from '@/utils/i18n';

export interface ProfileName {
  firstName?: string;
  lastName?: string;
  name?: string;
}

interface ConfirmIdentityProps {
  /** Display name derived from profile (firstName + lastName, or name, or username) */
  displayName: string;
  /** True if first and last name are properly set (required for bank linking) */
  hasFullName: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

/**
 * Reusable component to confirm user identity before sensitive flows (e.g. adding bank account).
 * Shows the user's name and requires affirmative confirmation before proceeding.
 */
type NameChoice = 'correct' | 'incorrect';

const ConfirmIdentity: React.FC<ConfirmIdentityProps> = ({
  displayName,
  hasFullName,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const navigate = useNavigate();
  const [nameChoice, setNameChoice] = useState<NameChoice | ''>('');

  if (!hasFullName) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
            <UserCheck className="w-6 h-6 text-amber-600 dark:text-amber-500" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">
              {i18n.__('confirmIdentity.title')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {i18n.__('confirmIdentity.requiredMessage')}
            </p>
          </div>
        </div>

        <Alert>
          <AlertDescription>
            {i18n.__('confirmIdentity.updateProfileMessage')}
          </AlertDescription>
        </Alert>

        <div className="flex space-x-2 pt-4">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            className="flex-1"
          >
            {i18n.__('common.cancel')}
          </Button>
          <Button
            onClick={() => navigate('/profile')}
            className="flex-1"
          >
            {i18n.__('confirmIdentity.updateProfile')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-full">
          <UserCheck className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-lg">
            {i18n.__('confirmIdentity.title')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {i18n.__('confirmIdentity.prompt')}
          </p>
        </div>
      </div>

      <div className="bg-muted/50 p-4 rounded-lg">
        <p className="text-sm font-medium text-muted-foreground mb-1">
          {i18n.__('confirmIdentity.yourName')}
        </p>
        <p className="text-lg font-semibold">{displayName}</p>
      </div>

      <p className="text-sm text-muted-foreground">
        {i18n.__('confirmIdentity.disclaimer')}
      </p>

      <RadioGroup
        value={nameChoice}
        onValueChange={(v) => setNameChoice(v as NameChoice)}
        className="space-y-3 pt-2"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="correct" id="name-correct" />
          <Label htmlFor="name-correct" className="cursor-pointer font-medium">
            {i18n.__('confirmIdentity.yes')}
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="incorrect" id="name-incorrect" />
          <Label htmlFor="name-incorrect" className="cursor-pointer font-medium">
            {i18n.__('confirmIdentity.no')}
          </Label>
        </div>
      </RadioGroup>

      <div className="flex space-x-2 pt-4">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={loading}
          className="flex-1"
        >
          {i18n.__('common.cancel')}
        </Button>
        <Button
          onClick={() => {
            if (nameChoice === 'correct') {
              onConfirm();
            } else if (nameChoice === 'incorrect') {
              navigate('/profile');
            }
          }}
          disabled={loading || !nameChoice}
          className="flex-1"
        >
          {i18n.__('common.submit')}
        </Button>
      </div>
    </div>
  );
};

export default ConfirmIdentity;

export interface ProfileNameSource {
  profile?: ProfileName;
  username?: string;
  /** PATCH /profile accepts these at the top level; some GET responses return them here too */
  firstName?: string;
  lastName?: string;
}

/** Derive display name and full-name status from profile/user object */
export function getDisplayNameAndStatus(user: ProfileNameSource | null): {
  displayName: string;
  hasFullName: boolean;
} {
  if (!user) {
    return { displayName: '', hasFullName: false };
  }
  const profile = user.profile || {};
  const firstName = (profile.firstName ?? user.firstName ?? '').trim();
  const lastName = (profile.lastName ?? user.lastName ?? '').trim();
  const name = (profile.name ?? '').trim();
  const username = (user.username ?? '').trim();

  const hasFullName = !!(firstName && lastName);
  const displayName =
    [firstName, lastName].filter(Boolean).join(' ') ||
    name ||
    username ||
    '';

  return { displayName, hasFullName };
}
